import { FC, useState } from 'react';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@mui/styles';
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Box,
  Theme
} from '@mui/material';
import { createOrderImages } from '../../../fetch';
import { ManyImagesUpload } from '../../../components';
import { IOrderPhoto } from '../../../models';
import { ALLOWED_IMAGE_EXTENSIONS, formatDate } from '../../../helpers';
import { DeleteForever } from '@mui/icons-material';

interface IPlaceOrderImagesProps {
  setFieldValue: (key: string, value: any) => void;
  photos?: IOrderPhoto[];
  photoGuids?: string[];
  isRequired?: boolean;
  setSelectedImage: (val: any) => void;
}

export const PlaceOrderImages: FC<IPlaceOrderImagesProps> = ({
  setFieldValue,
  photos,
  photoGuids,
  isRequired,
  setSelectedImage
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setUploading] = useState(false);
  return (
    <Box className={classes.photosContainer}>
      <Box paddingRight="24px">
        <ManyImagesUpload
          imageTitle="Photo(s)"
          isRequired={isRequired}
          isDisabled={isUploading}
          isLoading={isUploading}
          imageKey="place-order"
          fileType={ALLOWED_IMAGE_EXTENSIONS.join(', ')}
          handleFileChange={async (images: any[], files: File[]) => {
            try {
              setUploading(true);
              const formData = new FormData();
              files.forEach(file => {
                formData.append('images', file);
              });
              const res: string[] = await createOrderImages(formData);
              // combine guids together for create/update
              setFieldValue('photoGuids', [...(photoGuids || []), ...res]);
              // display the images
              setFieldValue('photos', [
                ...(photos || []),
                ...images.map(image => ({
                  photoGuid: '',
                  filePath: image.urlPath,
                  displayName: image.file.name,
                  uploadDate: new Date().toISOString()
                }))
              ]);
            } catch (error) {
              console.log(error);
              enqueueSnackbar('Error uploading images, please try again.', {
                variant: 'error'
              });
            } finally {
              setUploading(false);
            }
          }}
          images={photos?.map(p => p.photoGuid) ?? []}
        />
      </Box>
      {photos?.length > 0 && (
        <Box className={classes.photoTable}>
          <List>
            {photos.map((photo: IOrderPhoto, index) => {
              return (
                <ListItem
                  disablePadding
                  key={`${index}`}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      disabled={isUploading}
                      onClick={() => {
                        const confirm = window.confirm(
                          'Are you sure you want to remove this image?'
                        );

                        if (confirm) {
                          setFieldValue(
                            'photoGuids',
                            photos
                              .filter((_, photoIndex) => photoIndex !== index)
                              .map(p => p.photoGuid)
                          );
                          setFieldValue(
                            'photos',
                            photos.filter((_, photoIndex) => photoIndex !== index)
                          );
                        }
                      }}
                    >
                      <DeleteForever color="error" />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    sx={theme => ({
                      color: theme.palette.primary.main
                    })}
                    disabled={isUploading}
                    onClick={() => {
                      setSelectedImage(photo.filePath);
                    }}
                  >
                    <ListItemText
                      primary={photo.displayName ?? `image.png (${index + 1})`}
                      secondary={`Upload Date: ${formatDate(photo.uploadDate)}`}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  photosContainer: {
    display: 'flex',
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column'
    },
    '@media print': {
      display: 'block'
    }
  },
  photoTable: {
    borderTop: `2px solid ${theme.palette.secondary.main}`,
    [theme.breakpoints.up('md')]: {
      paddingLeft: '24px',
      borderLeft: `2px solid ${theme.palette.secondary.main}`,
      borderTop: 'none'
    }
  }
}));
